import { useState } from 'react'
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Box,
  Alert,
} from '@mui/material'
import { QuotationStatus } from '@typings'
import {
  QUOTATION_STATUS_LABELS,
  QUOTATION_STATUS_TRANSITIONS,
} from 'src/constants/status.constants'
import { LABELS } from '@constants'

interface QuotationStatusChangeDialogProps {
  open: boolean
  currentStatus: QuotationStatus | null
  onClose: () => void
  onSubmit: (newStatus: QuotationStatus, comment?: string) => Promise<void>
  isFinalized: boolean
}

export const QuotationStatusChangeDialog = ({
  open,
  currentStatus,
  onClose,
  onSubmit,
  isFinalized,
}: QuotationStatusChangeDialogProps) => {
  const [selectedStatus, setSelectedStatus] = useState<QuotationStatus | ''>('')
  const [comment, setComment] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const availableTransitions = currentStatus ? QUOTATION_STATUS_TRANSITIONS[currentStatus] || [] : []

  const handleClose = () => {
    if (!loading) {
      setSelectedStatus('')
      setComment('')
      setError(null)
      onClose()
    }
  }

  const handleSubmit = async () => {
    if (!selectedStatus) {
      setError('Selecteer een nieuwe status')
      return
    }

    setLoading(true)
    setError(null)

    try {
      await onSubmit(selectedStatus as QuotationStatus, comment.trim() || undefined)
      handleClose()
    } catch (err: any) {
      setError(err.message || 'Er is een fout opgetreden bij het wijzigen van de status')
    } finally {
      setLoading(false)
    }
  }

  if (!isFinalized) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Status wijzigen</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            Alleen gefinaliseerde offertes kunnen van status veranderen. Finaliseer eerst de offerte.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{LABELS.CANCEL}</Button>
        </DialogActions>
      </Dialog>
    )
  }

  if (availableTransitions.length === 0) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Status wijzigen</DialogTitle>
        <DialogContent>
          <Alert severity="info">
            Deze offerte kan niet meer van status veranderen. De huidige status is definitief.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{LABELS.CANCEL}</Button>
        </DialogActions>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Offerte status wijzigen</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            select
            label="Nieuwe status"
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value as QuotationStatus)}
            fullWidth
            disabled={loading}
            required>
            <MenuItem value="">Selecteer een status</MenuItem>
            {availableTransitions.map((status) => (
              <MenuItem key={status} value={status}>
                {QUOTATION_STATUS_LABELS[status]}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Opmerking (optioneel)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            multiline
            rows={4}
            fullWidth
            disabled={loading}
            helperText="Voeg een opmerking toe over deze statuswijziging"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {LABELS.CANCEL}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !selectedStatus}
          >
          {loading ? 'Bezig...' : 'Status wijzigen'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
